/* =========================================================================
   Gutsi - /pages/creators (the Creator Programme / careers page)

   WHY THIS FILE EXISTS
   The page's own inline <script> binds everything to data-gc-* attributes:
     [data-gc-slide]  [data-gc-ambassador-form]  [data-gc-ambassador-status]
     [data-gc-lightbox-modal]  [data-gc-lightbox-img]  [data-gc-swatch]
   None of those attributes are in the page markup any more - the elements
   carry plain classes instead (.carousel-slide, .gc-ambassador-form, ...).
   So on the live page:
     * the carousel IIFE hits `if(!n) return` and NEVER binds the arrows,
     * the form never gets its submit handler, so it falls through to a
       native GET and every applicant is silently lost,
     * `modal.addEventListener` runs on null and THROWS,
     * the swatches never swap the product shots.
   Maddie, 10 Sep 2026: "the clicking buttons not working ectt".

   The page body is Shopify page content, not a theme file, so it cannot be
   staged on a draft theme. Maddie asked for this on the draft, so the repair
   lives here instead: a theme asset that re-implements the behaviour against
   the classes that ARE in the markup. It ships when the draft is published.

   Safe to load site-wide: it bails immediately off this page, and it only
   ATTACHES behaviour - the inline script has already bailed by the time this
   runs, so nothing is double-bound.
   ========================================================================= */
(function () {
  'use strict';

  var CDN = 'https://cdn.shopify.com/s/files/1/0986/7860/4106/files/';
  var V = '?v=1764709884';

  /* The three finishes, in the order the swatches appear in the markup.
     The swatch hex values in the page are #E9D9B8, #C9C2B4, #4B4B4B.
     NOTE the second one is named "Dune" in the storefront but its image
     files are all called Pebble* - that mismatch is in Shopify Files, not
     a typo here. The header nav in sections/header-group.json uses the same
     Champagne / Dune / Granite naming. */
  var FINISHES = [
    { name: 'Champagne', front: 'ChampagneFrontHero.png', side: 'ChampagneSide.png', back: 'ChampagneBack.png' },
    { name: 'Dune',      front: 'PebbleFrontHero.png',    side: 'PebbleSide.png',    back: 'PebbleBack.png' },
    { name: 'Granite',   front: 'GraniteFrontHero.png',   side: 'GraniteSide.png',   back: 'GraniteBack.png' }
  ];

  function boot() {
    var root = document.getElementById('gc-creators1');
    if (!root) return;

    /* ---------------------------------------------------------------
       1. CAROUSEL
       Slides are position:absolute; left:50%, so with no JS they all
       stack on top of each other and only the last one is visible.
       This is the original coverflow maths, bound to .carousel-slide.
       --------------------------------------------------------------- */
    var modal = root.querySelector('.lightbox');
    var modalImg = modal ? modal.querySelector('img') : null;

    var slides = Array.prototype.slice.call(root.querySelectorAll('.carousel-slide'));
    var n = slides.length;

    if (n) {
      var active = 0;

      var render = function () {
        slides.forEach(function (slide, i) {
          var raw = (i - active + n) % n;
          if (raw > n / 2) raw -= n;
          var tx = 0, scale = 1, opacity = 1, z = 3, pe = 'auto';
          if (raw === 0) { tx = 0; scale = 1; opacity = 1; z = 3; }
          else if (raw === -1) { tx = -60; scale = 0.76; opacity = 0.55; z = 2; }
          else if (raw === 1) { tx = 60; scale = 0.76; opacity = 0.55; z = 2; }
          else if (raw < -1) { tx = -105; scale = 0.5; opacity = 0; z = 1; pe = 'none'; }
          else { tx = 105; scale = 0.5; opacity = 0; z = 1; pe = 'none'; }
          slide.style.transform = 'translateX(-50%) translateX(' + tx + '%) scale(' + scale + ')';
          slide.style.opacity = opacity;
          slide.style.zIndex = z;
          slide.style.pointerEvents = pe;
        });
      };

      var goTo = function (i) { active = ((i % n) + n) % n; render(); };

      root.querySelectorAll('.carousel-arrow.prev').forEach(function (b) {
        b.setAttribute('type', 'button');
        b.setAttribute('aria-label', 'Previous photo');
        b.addEventListener('click', function () { goTo(active - 1); });
      });
      root.querySelectorAll('.carousel-arrow.next').forEach(function (b) {
        b.setAttribute('type', 'button');
        b.setAttribute('aria-label', 'Next photo');
        b.addEventListener('click', function () { goTo(active + 1); });
      });

      /* The copy says "Click a photo for a closer look", so the active
         slide opens the lightbox and the side slides just come forward. */
      slides.forEach(function (slide, i) {
        slide.addEventListener('click', function () {
          if (i !== active) { goTo(i); return; }
          var img = slide.querySelector('img');
          if (img && modal && modalImg) {
            modalImg.src = img.getAttribute('src');
            modalImg.alt = img.getAttribute('alt') || 'Enlarged view';
            modal.classList.add('open');
          }
        });
      });

      render();
    }

    /* ---------------------------------------------------------------
       2. LIGHTBOX
       --------------------------------------------------------------- */
    if (modal) {
      var closeModal = function () { modal.classList.remove('open'); };
      modal.addEventListener('click', closeModal);
      var closeBtn = modal.querySelector('.lightbox-close');
      if (closeBtn) {
        closeBtn.setAttribute('type', 'button');
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.addEventListener('click', function (e) { e.stopPropagation(); closeModal(); });
      }
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeModal();
      });
    }

    /* ---------------------------------------------------------------
       3. COLOUR SWATCHES
       All three finishes DO have front/side/back renders in Shopify
       Files - an earlier pass wrongly hid these after a filename search
       only turned up the Champagne set. Wire them up instead.
       --------------------------------------------------------------- */
    var swatches = Array.prototype.slice.call(root.querySelectorAll('.swatch'));
    var mainWrap = root.querySelector('.product-image-wrap.main');
    var mainImg = mainWrap ? mainWrap.querySelector('img') : null;
    var otherImgs = Array.prototype.slice.call(
      root.querySelectorAll('.product-image-wrap:not(.main) img')
    );
    var caption = root.querySelector('.product-caption');

    if (swatches.length && mainImg) {
      swatches.forEach(function (sw, i) {
        var finish = FINISHES[i];
        if (!finish) return;
        sw.setAttribute('role', 'button');
        sw.setAttribute('tabindex', '0');
        sw.setAttribute('aria-label', finish.name);
        sw.style.cursor = 'pointer';

        var select = function () {
          mainImg.src = CDN + finish.front + V;
          mainImg.alt = 'Gutsi device in ' + finish.name + ', front view';
          if (otherImgs[0]) {
            otherImgs[0].src = CDN + finish.side + V;
            otherImgs[0].alt = 'Gutsi device in ' + finish.name + ', side view';
          }
          if (otherImgs[1]) {
            otherImgs[1].src = CDN + finish.back + V;
            otherImgs[1].alt = 'Gutsi device in ' + finish.name + ', back view';
          }
          if (caption) caption.textContent = finish.name;
          swatches.forEach(function (s) { s.classList.remove('active'); });
          sw.classList.add('active');
        };

        sw.addEventListener('click', select);
        sw.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); }
        });
      });
    }

    /* ---------------------------------------------------------------
       4. COPY FIX
       Maddie, 11 Sep 2026: 'get rid of "then some" doesnt make ense'.
       The kicker reads "GET paID, THEN SOME" in the page source (the
       CSS uppercases it). Set from here rather than editing the page
       body, so this stays on the draft with the rest of the repair.
       --------------------------------------------------------------- */
    var payKicker = root.querySelector('.pay-band .kicker');
    if (payKicker && /then some/i.test(payKicker.textContent)) {
      payKicker.textContent = 'Get paid';
    }

    /* ---------------------------------------------------------------
       5. AMBASSADOR FORM -> Klaviyo list SQV47d ("Ambassador Applicants")
       Verified 11 Sep 2026: the list exists and is single opt-in, which
       matches the consent line under the form. The endpoints are
       Klaviyo's public client API, keyed by company_id - no secret here.
       --------------------------------------------------------------- */
    var KLAVIYO_COMPANY_ID = 'UWab4u';
    var AMBASSADOR_LIST_ID = 'SQV47d';
    var KLAVIYO_REVISION = '2026-07-15';

    root.querySelectorAll('.gc-ambassador-form').forEach(function (form) {
      if (form.dataset.gcBound) return;
      form.dataset.gcBound = '1';

      var wrapper = form.parentElement;
      var statusEl = wrapper ? wrapper.querySelector('.gc-ambassador-status') : null;
      var submitBtn = form.querySelector('button[type="submit"]');

      function showStatus(message, isError) {
        if (!statusEl) return;
        statusEl.textContent = message;
        statusEl.style.display = 'block';
        statusEl.classList.toggle('is-error', !!isError);
        statusEl.setAttribute('role', isError ? 'alert' : 'status');
      }

      function field(name) {
        var el = form.querySelector('[name="' + name + '"]');
        return el ? el.value.trim() : '';
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var firstName = field('first_name');
        var email = field('email');
        var linkedin = field('linkedin');

        /* The form carries novalidate, so the required attributes do
           nothing on their own - this is the only validation there is. */
        if (!email || email.indexOf('@') === -1) {
          showStatus('Please enter a valid email address.', true);
          return;
        }
        if (!linkedin) {
          showStatus('Please add your LinkedIn profile URL.', true);
          return;
        }

        submitBtn.disabled = true;
        var originalLabel = submitBtn.textContent;
        submitBtn.textContent = 'Submitting…';
        if (statusEl) statusEl.style.display = 'none';

        var headers = {
          'Content-Type': 'application/json',
          'revision': KLAVIYO_REVISION
        };

        fetch('https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: firstName,
                properties: {
                  'LinkedIn URL': linkedin,
                  'Ambassador Applicant': true
                }
              }
            }
          })
        })
        .then(function (res) {
          if (!res.ok && res.status !== 202) throw new Error('profile create failed');
          return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        first_name: firstName,
                        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                      }
                    }
                  }
                },
                relationships: {
                  list: { data: { type: 'list', id: AMBASSADOR_LIST_ID } }
                }
              }
            })
          });
        })
        .then(function (res) {
          if (!res.ok && res.status !== 202) throw new Error('subscribe failed');
          form.style.display = 'none';
          showStatus("You're on the list. We'll be in touch soon.", false);
        })
        .catch(function () {
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
          showStatus('Something went wrong, please try again, or email us directly.', true);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
